import { CmsValue } from 'core/type/BootstrapConfig.types';
import { SortMatrix } from 'gql/autogen/SortMatrix';
import { Sort } from 'gql/autogen/Sort';
import { ReviewSortDisplayItem, reviewSortDisplayKeyList, ReviewSortEnum, ReviewValueFilter } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.types';
import { ValueMatrix } from 'gql/autogen/ValueMatrix';
import { StringFormatter } from '@agoda/react-handyman';
import { SortItemProps } from 'component/common/SortItem/SortItem';
import { FilterCode } from 'gql/autogen/FilterCode';
import { FilterMatrix } from 'gql/autogen/FilterMatrix';
import { Filters } from 'gql/autogen/Filters';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';
import { Matrix } from 'gql/autogen/Matrix';

const createReviewSortItem = (reviewSortType: ReviewSortEnum, cms: CmsValue, currentSort?: Sort): SortItemProps => {
    const sortItem = ReviewSortDisplayItem[reviewSortType];

    return {
        label: cms[sortItem.cmsId],
        type: reviewSortType,
        code: sortItem.code,
        order: sortItem.order,
        name: 'sort',
        selected: currentSort?.code === sortItem.code && currentSort.order === sortItem.order,
    };
};

export const getReviewSortItems = (cms: CmsValue, availableSortMatrix: SortMatrix, currentSort?: Sort): SortItemProps[] => {
    if (!availableSortMatrix?.availableOrder?.length) {
        return [];
    }
    const existSortItems: SortItemProps[] = [];

    reviewSortDisplayKeyList.forEach((key) => {
        const code = ReviewSortDisplayItem[key].code;
        const order = ReviewSortDisplayItem[key].order;

        const isValidDefaultSort = code === SortCode.Default && order === SortOrder.Default;
        const isValidSortCode = code !== SortCode.Default && availableSortMatrix.availableCode?.includes(code);
        const isValidSortOrder = order !== SortOrder.Default && availableSortMatrix.availableOrder?.includes(order);

        if (isValidDefaultSort || (isValidSortCode && isValidSortOrder)) {
            existSortItems.push(createReviewSortItem(key, cms, currentSort));
        }
    });

    const fallbackSortItems = createReviewSortItem(ReviewSortEnum.MostRelevant, cms, currentSort);
    return existSortItems || fallbackSortItems;
};

export const selectReviewRatingFilters = (reviewFilterMatrix: FilterMatrix, contentLocale: string, cms: CmsValue): ReviewValueFilter[] => {
    const ratingFilterMatrix = reviewFilterMatrix.valueFilterMatrix?.find((item) => item.code === FilterCode.Rating) || {};
    const availableRatingFilters = ratingFilterMatrix.availableValue;
    if (!availableRatingFilters?.length) {
        return [];
    }
    return availableRatingFilters
        .map((rating) => (rating.id && typeof rating.count !== 'undefined' && rating.count >= 0 ? rating : (undefined as unknown as ValueMatrix)))
        .filter((rating) => !!rating)
        .map((rating) => {
            const label = StringFormatter.format(Number(rating.id) > 1 && contentLocale !== 'en' ? cms[276377] : cms[275844], Number(rating.id));
            return {
                code: FilterCode.Rating,
                id: rating.id,
                count: rating.count,
                label,
                type: `${FilterCode.Rating}_${rating.id}`,
            } as ReviewValueFilter;
        })
        .sort((a, b) => Number(b.id) - Number(a.id));
};

export const selectReviewImageFilters = (reviewFilterMatrix: FilterMatrix, cms: CmsValue): ReviewValueFilter[] => {
    const imageFilterMatrix = reviewFilterMatrix.valueFilterMatrix?.find((item) => item.code === FilterCode.ReviewWithImage) || {};
    const availableImageFilters = imageFilterMatrix.availableValue;
    if (!availableImageFilters?.length) {
        return [];
    }
    return availableImageFilters
        .map((image) => (image.id && typeof image.count !== 'undefined' && image.count >= 0 ? image : (undefined as unknown as ValueMatrix)))
        .filter((image) => !!image && image.id === 'True')
        .map((image) => {
            return {
                code: FilterCode.ReviewWithImage,
                id: image.id,
                count: image.count,
                label: cms[276916],
                type: `${FilterCode.ReviewWithImage}_${image.id}`,
            } as ReviewValueFilter;
        });
};

export const getAvailableFilters = (reviewFilterMatrix: FilterMatrix, contentLocale: string, cms: CmsValue): ReviewValueFilter[] => {
    const ratingFilters = selectReviewRatingFilters(reviewFilterMatrix, contentLocale, cms);
    const imageFilters = selectReviewImageFilters(reviewFilterMatrix, cms);
    return imageFilters.concat(ratingFilters);
};

export const getDisplayOrderFilters = (availableReviewFilters: ReviewValueFilter[], currentSelectedFiltersType: string[]): ReviewValueFilter[] => {
    const selectedFilters = availableReviewFilters.filter((item) => currentSelectedFiltersType.includes(item.type));
    const notSelectedFilters = availableReviewFilters.filter((item) => !currentSelectedFiltersType.includes(item.type));
    if (selectedFilters.length === 0) {
        return availableReviewFilters;
    }
    return selectedFilters.concat(notSelectedFilters);
};

export const getRequestFormatFilters = (currentFilters: Filters, selectedFilters: ReviewValueFilter[]): Filters => {
    const selectedRatingFilters = selectedFilters.filter((item) => item.code === FilterCode.Rating);
    const selectedImageFilters = selectedFilters.filter((item) => item.code === FilterCode.ReviewWithImage);
    const valueFilters = [];

    selectedRatingFilters.length > 0 && valueFilters.push({ code: FilterCode.Rating, values: selectedRatingFilters.map((item) => item.id) });
    selectedImageFilters.length > 0 && valueFilters.push({ code: FilterCode.ReviewWithImage, values: selectedImageFilters.map((item) => item.id) });
    return {
        ...currentFilters,
        valueFilters,
    };
};

export const getSelectedFiltersType = (currentFilters: Filters): string[] => {
    const selectedFiltersTextList: string[] = [];
    currentFilters.valueFilters.forEach((filter) => {
        filter.values.forEach((value) => {
            selectedFiltersTextList.push(`${filter.code}_${value}`);
        });
    });
    return selectedFiltersTextList;
};

export const getReviewTotalAndAverageScore = (reviewRatingFilters: ReviewValueFilter[]): { totalReview: number; averageScore: string } => {
    if (!reviewRatingFilters || reviewRatingFilters.length === 0) {
        return { totalReview: 0, averageScore: '' };
    }

    const totalReview = reviewRatingFilters.reduce((sum, review) => sum + review.count, 0);
    const averageScore = reviewRatingFilters.reduce((sum, review) => sum + Number(review.id) * review.count, 0) / totalReview;
    const roundedReviewAverageScore = averageScore.toFixed(1);
    return { totalReview, averageScore: roundedReviewAverageScore };
};

export const getReviewInfo = (locale: string, cms: CmsValue, matrix?: Matrix): { totalReview: number; averageScore: string } => {
    const reviewFilterMatrix = matrix?.filter || {};
    const reviewRatingFilters = selectReviewRatingFilters(reviewFilterMatrix, locale, cms);
    return getReviewTotalAndAverageScore(reviewRatingFilters);
};
